import * as React from "react";
import ReactDatePicker from "react-date-picker";
import { LocalDate, YearMonth } from "js-joda";
import { isArray } from "util";
import "./DateWidget.css";

interface DatePickerProps<T> {
    className?: string;
    onChange: (value: T | null) => void;
    value: T | null;
    forMobile: boolean;
}

export interface DatePickerWidgetProps extends DatePickerProps<LocalDate> {
}

export const CalendarIcon = () => {
    return <i className="fa fa-calendar" />;
};

export class DatePickerWidget extends React.Component<DatePickerWidgetProps> {


    parseInputStringToLocalDate = (val: string | null) => {
        if (val === null) {
            return null;
        }
        const vals = val.split("-");

        if (vals.length !== 3) {
            return null; // something weird happened, nullify the answer
        }

        const year = parseInt(vals[0], 10);
        const month = parseInt(vals[1], 10);
        const day = parseInt(vals[2], 10);

        return LocalDate.of(year, month, day);
    }

    handleMobileValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        const date = this.parseInputStringToLocalDate(e.target.value);
        const target = e.nativeEvent.target;
        function delayedClear() { (target as HTMLInputElement).defaultValue = ""; }
        window.setTimeout(delayedClear, 0);
        this.props.onChange(date);
    }

    render() {
        let val: Date | undefined;
        if (this.props.value === null) {
            val = undefined;
        } else {
            const localDate = this.props.value;
            val = new Date(localDate.year(), localDate.monthValue() - 1, localDate.dayOfMonth());
        }
        return (
            <div>
                {
                    this.props.forMobile ?
                    <input
                        type="date"
                        className="DateWidget__mobileInput"
                        value={this.props.value === null ? undefined : this.props.value.toString()}
                        onInput={this.handleMobileValue}
                    />
                    :
                    <ReactDatePicker
                        className={this.props.className}
                        onChange={(e) => {
                            if (e === null || e === undefined || (isArray(e) && e.length === 0)) {
                                this.props.onChange(null);
                                return;
                            }
                            const date = isArray(e) ? e[0] : e;

                            const year = date.getFullYear();
                            const month = date.getMonth() + 1;
                            const day = date.getDate();

                            this.props.onChange(LocalDate.of(year, month, day));
                        }}
                        value={val}
                        view={"decade"}
                        calendarType={"US"}
                        calendarIcon={<CalendarIcon />}
                    />
                }
            </div>
        );
    }
}

export interface YearPickerWidgetProps  extends DatePickerProps<number> {
}

export class YearPickerWidget extends React.Component<YearPickerWidgetProps> {

    parseInputStringToLocalDate = (val: string | null) => {
        if (val === null || val === "") {
            return null;
        }

        const year = parseInt(val, 10);

        return year;
    }

    handleMobileValue = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const date = this.parseInputStringToLocalDate(e.target.value);
        const target = e.nativeEvent.target;
        function delayedClear() { (target as HTMLInputElement).defaultValue = ""; }
        window.setTimeout(delayedClear, 0);
        this.props.onChange(date);
    }

    renderOptions = () => {
        const map = [];
        for (let i = 1950; i <= 2040; i++) {
            map.push(<option key={i} value={i}>{i}</option>);
        }
        return map;
    }

    resetYear = () => {
        this.props.onChange(null);
        this.forceUpdate();
    }

    render() {
        return (
            <div>
                {
                    this.props.forMobile ?
                    <div className="dateWidget__yearContainer">
                        <select
                            className="DateWidget__mobileInput"
                            value={this.props.value === null ? 0 : this.props.value.toString()}
                            onChange={this.handleMobileValue}
                        >
                            <option hidden={true} selected={true} value={0}> select a year </option>
                            {this.renderOptions()}
                        </select>
                        {this.props.value === null ? null : <div onClick={this.resetYear} className="DateWidget__clearYear" />}
                    </div>
                    :
                    <ReactDatePicker
                        className={this.props.className}
                        onChange={(e) => {
                            if (e === null || e === undefined || (isArray(e) && e.length === 0)) {
                                this.props.onChange(null);
                                return;
                            }

                            const date = isArray(e) ? e[0] : e;

                            const year = date.getFullYear();

                            this.props.onChange(year);
                        }}
                        value={this.props.value === null ? undefined : new Date(this.props.value, 0, 1)}
                        view={"decade"}
                        calendarType={"US"}
                        maxDetail={"decade"}
                        calendarIcon={<CalendarIcon />}
                    />
                }
            </div>
        );
    }
}

export interface YearMonthPickerWidgetProps extends DatePickerProps<YearMonth> {
}

export class YearMonthPickerWidget extends React.Component<YearMonthPickerWidgetProps> {

    parseInputStringToYearMonth = (val: string | null) => {
        if (val === null) {
            return null;
        }
        const vals = val.split("-");

        if (vals.length !== 2) {
            return null; // something weird happened, nullify the answer
        }

        const year = parseInt(vals[0], 10);
        const month = parseInt(vals[1], 10);

        return YearMonth.of(year, month);
    }

    handleMobileValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        const date = this.parseInputStringToYearMonth(e.target.value);
        const target = e.nativeEvent.target;
        function delayedClear() { (target as HTMLInputElement).defaultValue = ""; }
        window.setTimeout(delayedClear, 0);
        this.props.onChange(date);
    }

    render() {
        return (
            <div>
                {
                    this.props.forMobile ?
                    <input
                        type="month"
                        className="DateWidget__mobileInput"
                        value={this.props.value === null ? undefined : this.props.value.toString()}
                        onInput={this.handleMobileValue}
                    />
                    :
                    <ReactDatePicker
                        className={this.props.className}
                        onChange={(e) => {
                            if (e === null || e === undefined || (isArray(e) && e.length === 0)) {
                                this.props.onChange(null);
                                return;
                            }

                            const date = isArray(e) ? e[0] : e;

                            const year = date.getFullYear();
                            const month = date.getMonth() + 1;

                            this.props.onChange(YearMonth.of(year, month));
                        }}
                        value={this.props.value === null ? undefined : new Date(this.props.value.year(), this.props.value.monthValue() - 1, 1)}
                        view={"decade"}
                        calendarType={"US"}
                        maxDetail={"year"}
                        calendarIcon={<CalendarIcon />}
                    />
                }
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/widgets/DateWidget.tsx